import { useRef, useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { TIER_PERSONAS, getPersonaDisplayName, PERSONA_GLOW_COLORS } from '../constants/personas'
import { PersonaIcon } from './PersonaIcon'
import { useTheme } from '../contexts/ThemeContext'
import type { Worker, Shard } from './GraphView'

const STATUS_COLORS: Record<string, string> = {
  TODO: 'text-cosmic-star',
  READY: 'text-cyan-400',
  IN_PROGRESS: 'text-teal-400',
  REVIEW: 'text-purple-400',
  DONE: 'text-gray-400',
  BLOCKED: 'text-cosmic-plasma',
}

function getShardOrigin(shard: Shard): string {
  if (shard.type === 'SPEC' || shard.id.startsWith('SPEC')) {
    return 'architect'
  }
  return 'prism'
}

function getShardTarget(shard: Shard): string | null {
  if (shard.type === 'SPEC' || shard.id.startsWith('SPEC')) {
    return null // SPECs stay at architect
  }
  return shard.assignee || null
}

interface ShardParticleProps {
  shard: Shard
  onClick?: () => void
}

function ShardParticle({ shard, onClick }: ShardParticleProps) {
  const isSpec = shard.type === 'SPEC' || shard.id.startsWith('SPEC')
  const colorClass = STATUS_COLORS[shard.status] || STATUS_COLORS[shard.status?.toUpperCase()] || 'text-gray-400'
  const origin = getShardOrigin(shard)
  const target = getShardTarget(shard)

  return (
    <motion.div
      data-testid={`shard-particle-${shard.id}`}
      data-origin={origin}
      data-target={target || undefined}
      className={`shard-particle ${colorClass} cursor-pointer transition-all hover:scale-110 absolute`}
      onClick={onClick}
      title={`${shard.id}: ${shard.status}`}
      animate={{
        x: [0, Math.random() * 10 - 5, 0],
        y: [0, Math.random() * 10 - 5, 0],
      }}
      transition={{
        duration: 2 + Math.random(),
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      <div className={`flex items-center justify-center text-lg font-mono border-2 border-current rounded ${isSpec ? 'w-10 h-10' : 'w-8 h-8 rounded-full'}`}>
        {isSpec ? '⬡' : '●'}
      </div>
      <div className="text-xs mt-1 text-center opacity-70">{shard.id}</div>
    </motion.div>
  )
}

interface HierarchicalLayoutProps {
  workers: Worker[]
  shards: Shard[]
  onWorkerClick?: (workerId: string) => void
  onShardClick?: (shardId: string) => void
}

interface PersonaGroupProps {
  persona: string
  workers: Worker[]
  onWorkerClick?: (workerId: string) => void
  setPersonaRef: (el: HTMLDivElement | null) => void
  setWorkerRef: (workerId: string) => (el: HTMLDivElement | null) => void
}

function PersonaGroup({ persona, workers, onWorkerClick, setPersonaRef, setWorkerRef }: PersonaGroupProps) {
  const hasWorkers = workers.length > 0
  const { theme } = useTheme()
  const isCorporate = theme === 'corporate'
  const displayName = getPersonaDisplayName(persona, isCorporate)

  return (
    <div
      ref={setPersonaRef}
      data-testid={`persona-group-${persona}`}
      className="persona-group flex flex-col items-center gap-2 p-4 min-w-[120px]"
    >
      <div className="text-xs text-cosmic-border uppercase tracking-wider">
        {displayName}
      </div>

      <div className="workers-container flex flex-wrap gap-2 justify-center min-h-[100px] max-lg:min-h-[60px] items-center">
        {hasWorkers ? (
          workers.map((worker) => {
            const glowColor = PERSONA_GLOW_COLORS[worker.persona?.toLowerCase() || 'nexus'] || 'rgba(59, 130, 246, 0.5)'
            const stateClass = worker.state === 'active'
              ? ''
              : 'opacity-40 grayscale'

            return (
              <div
                key={worker.id}
                ref={setWorkerRef(worker.id)}
                data-testid={`worker-node-${worker.id}`}
                className={`worker-node state-${worker.state} relative cursor-pointer transition-all`}
                onClick={() => onWorkerClick?.(worker.id)}
              >
                <div className="relative">
                  {worker.state === 'active' && (
                    <div className="heartbeat-pulse absolute -inset-2 rounded-full bg-cosmic-nebula opacity-20 animate-pulse-slow" />
                  )}
                  <div
                    className={`relative z-10 flex flex-col items-center justify-center p-3 rounded-full w-16 h-16 transition-all ${stateClass}`}
                    // Glow via drop-shadow (hugs the icon shape) not box-shadow (a hard
                    // circular halo on the invisible round container).
                    style={worker.state === 'active' ? { filter: `drop-shadow(0 0 4px ${glowColor}) drop-shadow(0 0 8px ${glowColor})` } : undefined}
                  >
                    <PersonaIcon persona={worker.persona} size="lg" />
                  </div>
                </div>
                <div className="text-center mt-1 text-xs font-medium">{worker.name}</div>
              </div>
            )
          })
        ) : (
          <div className="ghost-outline relative opacity-20 rounded-full w-16 h-16 flex items-center justify-center">
            <PersonaIcon persona={persona} size="lg" className="opacity-50" />
          </div>
        )}
      </div>
    </div>
  )
}

export function HierarchicalLayout({
  workers,
  shards,
  onWorkerClick,
  onShardClick,
}: HierarchicalLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const personaRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const workerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({})

  const setPersonaRef = useCallback((persona: string) => (el: HTMLDivElement | null) => {
    personaRefs.current[persona] = el
  }, [])

  const setWorkerRef = useCallback((workerId: string) => (el: HTMLDivElement | null) => {
    workerRefs.current[workerId] = el
  }, [])

  // Calculate positions when layout changes
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const newPositions: Record<string, { x: number; y: number }> = {}
    const containerRect = container.getBoundingClientRect()

    // Get persona group positions
    Object.entries(personaRefs.current).forEach(([persona, el]) => {
      if (el) {
        const rect = el.getBoundingClientRect()
        newPositions[`persona-${persona}`] = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        }
      }
    })

    // Get worker positions
    Object.entries(workerRefs.current).forEach(([workerId, el]) => {
      if (el) {
        const rect = el.getBoundingClientRect()
        newPositions[`worker-${workerId}`] = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        }
      }
    })

    setPositions(newPositions)
  }, [workers, shards])

  // Group workers by persona
  const workersByPersona = workers.reduce((acc, worker) => {
    const persona = worker.persona.toLowerCase()
    if (!acc[persona]) acc[persona] = []
    acc[persona].push(worker)
    return acc
  }, {} as Record<string, Worker[]>)

  return (
    <div ref={containerRef} className="hierarchical-layout w-full h-full max-lg:h-auto flex flex-col justify-around max-lg:justify-start max-lg:gap-4 py-8 relative">
      {/* Tier rows */}
      {[1, 2, 3].map((tier) => (
        <div
          key={tier}
          data-testid={`tier-${tier}`}
          className="tier flex justify-center gap-8 max-lg:flex-wrap max-lg:gap-4"
        >
          {(TIER_PERSONAS[tier] || []).map((persona) => (
            <PersonaGroup
              key={persona}
              persona={persona}
              workers={workersByPersona[persona] || []}
              onWorkerClick={onWorkerClick}
              setPersonaRef={setPersonaRef(persona)}
              setWorkerRef={setWorkerRef}
            />
          ))}
        </div>
      ))}

      {/* Shards layer - positioned absolutely */}
      <div className="shards-layer absolute inset-0 pointer-events-none">
        {shards.map((shard) => {
          const origin = getShardOrigin(shard)
          const target = getShardTarget(shard)

          // Get position - target worker if assigned, otherwise origin persona
          const targetKey = target ? `worker-${target}` : `persona-${origin}`
          const pos = positions[targetKey] || positions[`persona-${origin}`]

          // Don't render if no valid position yet (prevents top-left flash)
          if (!pos) return null

          // Get top tier position for initial animation origin
          // Shards animate from the top tier (nexus position) to their destination
          const topTierPos = positions['persona-nexus'] || positions['persona-voyager']
          const initialY = topTierPos ? topTierPos.y : 50 // Fallback to top if no tier 1 position

          return (
            <motion.div
              key={shard.id}
              className="pointer-events-auto absolute"
              initial={{
                left: pos.x,  // Same x as destination
                top: initialY,  // Start from top tier
              }}
              animate={{
                left: pos.x,
                top: pos.y,
              }}
              transition={{
                type: 'spring',
                stiffness: 100,
                damping: 20,
                duration: 0.5,
              }}
              style={{
                transform: 'translate(-50%, -50%)',
              }}
            >
              <ShardParticle
                shard={shard}
                onClick={() => onShardClick?.(shard.id)}
              />
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
